import * as React from 'react';
import { useMemo } from 'react';
import {
  Table,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
} from 'react-aria-components';
import type { Selection, Key } from 'react-aria-components';
import classnames from 'classnames';
import styles from './AtollonTable.module.css';
import { AtollonCheckbox } from '../Checkbox/AtollonCheckbox';

export interface IAtollonTableColumn<T = any> {
  key: string;
  label: string;
  dataKey?: string;
  width?: number | string;
  sortable?: boolean;
  renderCell?: (row: T) => React.ReactNode;
}

export interface IAtollonTableProps<T = any> {
  columns: IAtollonTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  selectionMode?: 'multiple';
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  onReorder?: (keys: string[]) => void;
  className?: string;
  'aria-label'?: string;
}

export function AtollonTable<T = any>(props: IAtollonTableProps<T>) {
  const {
    columns,
    rows,
    getRowKey,
    selectionMode,
    selectedKeys,
    onSelectionChange,
    className,
    'aria-label': ariaLabel,
  } = props;

  // Track selection state for manual checkbox management
  const [internalSelectedKeys, setInternalSelectedKeys] = React.useState<Set<string>>(
    selectedKeys || new Set()
  );

  const selectedKeysForAria = useMemo<Selection>(
    () => internalSelectedKeys as Set<Key>,
    [internalSelectedKeys]
  );

  // Sync with external selectedKeys prop
  React.useEffect(() => {
    if (selectedKeys) {
      setInternalSelectedKeys(selectedKeys);
    }
  }, [selectedKeys]);

  // Handle selection change - convert React Aria Selection to Set<string>
  const handleSelectionChange = React.useCallback(
    (selection: Selection) => {
      if (selection === 'all') {
        const allKeys = new Set(rows.map((row) => getRowKey(row)));
        setInternalSelectedKeys(allKeys);
        onSelectionChange?.(allKeys);
      } else {
        const keysSet = new Set(Array.from(selection) as string[]);
        setInternalSelectedKeys(keysSet);
        onSelectionChange?.(keysSet);
      }
    },
    [onSelectionChange, rows, getRowKey]
  );

  // Handle select all checkbox
  const allSelected = useMemo(() => {
    if (rows.length === 0) return false;
    return internalSelectedKeys.size === rows.length && rows.every((row) => internalSelectedKeys.has(getRowKey(row)));
  }, [internalSelectedKeys, rows, getRowKey]);

  const someSelected = useMemo(() => {
    return internalSelectedKeys.size > 0 && !allSelected;
  }, [internalSelectedKeys, allSelected]);

  const handleSelectAll = React.useCallback(
    (isSelected: boolean) => {
      if (isSelected) {
        const allKeys = new Set(rows.map((row) => getRowKey(row)));
        setInternalSelectedKeys(allKeys);
        onSelectionChange?.(allKeys);
      } else {
        const emptySet = new Set<string>();
        setInternalSelectedKeys(emptySet);
        onSelectionChange?.(emptySet);
      }
    },
    [rows, getRowKey, onSelectionChange]
  );

  // Handle individual row selection
  const handleRowSelection = React.useCallback(
    (rowKey: string, isSelected: boolean) => {
      const newSelection = new Set(internalSelectedKeys);
      if (isSelected) {
        newSelection.add(rowKey);
      } else {
        newSelection.delete(rowKey);
      }
      setInternalSelectedKeys(newSelection);
      onSelectionChange?.(newSelection);
    },
    [internalSelectedKeys, onSelectionChange]
  );

  // Convert rows to objects for React Aria Components (requires object type)
  const rowItems = useMemo(() => {
    return rows.map((row) => ({
      ...row,
      __rowKey: getRowKey(row),
    })) as Array<T & { __rowKey: string }>;
  }, [rows, getRowKey]);

  const tableClassName = classnames(styles.atollonTable, className);

  return (
    <div className={styles.tableWrapper}>
      <Table
        aria-label={ariaLabel || 'Table'}
        selectionMode={selectionMode || 'none'}
        selectedKeys={selectedKeysForAria}
        onSelectionChange={handleSelectionChange}
        className={tableClassName}
      >
        <TableHeader>
          {selectionMode === 'multiple' && (
            <Column>
              <AtollonCheckbox
                isSelected={allSelected}
                isIndeterminate={someSelected}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </Column>
          )}
          {columns.map((column) => (
            <Column
              key={column.key}
              style={column.width ? { width: column.width } : undefined}
              isRowHeader={true}
            >
              {column.label}
            </Column>
          ))}
        </TableHeader>
        <TableBody items={rowItems}>
          {(rowItem) => {
            const row = rowItem as T & { __rowKey: string };
            const rowKey = row.__rowKey;
            const isRowSelected = internalSelectedKeys.has(rowKey);

            return (
              <Row key={rowKey} id={rowKey}>
                {selectionMode === 'multiple' && (
                  <Cell>
                    <AtollonCheckbox
                      isSelected={isRowSelected}
                      onChange={(isSelected) => handleRowSelection(rowKey, isSelected)}
                      aria-label={`Select row ${rowKey}`}
                    />
                  </Cell>
                )}
                {columns.map((column) => {
                  const content = column.renderCell
                    ? column.renderCell(row)
                    : (row as any)[column.dataKey || column.key] ?? '';

                  return (
                    <Cell key={column.key}>
                      {content}
                    </Cell>
                  );
                })}
              </Row>
            );
          }}
        </TableBody>
      </Table>
    </div>
  );
}
